/**
 * triageService.js
 *
 * Supabase writes for the Get Care Now flow.
 * All functions are fire-and-forget safe — errors are swallowed silently
 * so the UI is never blocked by instrumentation failures.
 *
 * API:
 *   saveTriageSession(params)     → Promise<string|null>   session UUID or null
 *   logProviderImpressions(...)   → Promise<void>
 *   logProviderClick(...)         → Promise<void>
 *   saveCareOutcome(...)          → Promise<void>
 */

import { supabase } from '../supabase';

/**
 * Map triage care_level to nudge display level.
 * emergency_now is excluded — no follow-up nudge for ER dispatches.
 */
export const CARE_LEVEL_TO_NUDGE = {
  care_today: 'urgent',
  care_soon:  'soon',
  home_care:  'home',
};

/**
 * Persist a completed triage session.
 * Works for anonymous users (user_id null) and signed-in users.
 *
 * @param {{ userId: string|null, protocolId: string, personType: string,
 *           ageBand: string|null, answers: object, triageResult: object }} params
 * @returns {Promise<string|null>} session UUID or null on error
 */
export async function saveTriageSession({ userId, protocolId, personType, ageBand, answers, triageResult }) {
  try {
    const row = {
      user_id:          userId || null,
      symptom_cluster:  protocolId,
      person_type:      personType || 'self',
      age_band:         ageBand || null,
      structured_answers: answers || {},
      care_level:       triageResult.careLevel,
      reason_codes:     triageResult.reasonCodes || [],
      suggested_care_sites: triageResult.suggestedCareSites || [],
    };

    const { data, error } = await supabase
      .from('triage_sessions')
      .insert(row)
      .select('id')
      .single();

    if (error) {
      console.warn('[triageService] saveTriageSession error:', error.message);
      return null;
    }
    return data?.id || null;
  } catch (err) {
    console.warn('[triageService] saveTriageSession exception:', err.message);
    return null;
  }
}

/**
 * Log that a set of providers was shown on the result screen.
 * One row per provider, shown_at = now(), clicks null until tapped.
 *
 * @param {string} triageSessionId
 * @param {string|null} userId
 * @param {object[]} providers
 */
export async function logProviderImpressions(triageSessionId, userId, providers) {
  if (!triageSessionId || !providers || providers.length === 0) return;
  try {
    const rows = providers.map((p, i) => ({
      triage_session_id: triageSessionId,
      user_id:           userId || null,
      provider_npi:      p.npi || null,
      provider_name:     p.name || null,
      rank:              i + 1,
      route_score:       p.score || null,
    }));

    const { error } = await supabase
      .from('route_recommendations')
      .insert(rows);

    if (error) {
      console.warn('[triageService] logProviderImpressions error:', error.message);
    }
  } catch (err) {
    console.warn('[triageService] logProviderImpressions exception:', err.message);
  }
}

/**
 * Record a click action on a provider card.
 * Updates the matching route_recommendations row for this session + NPI.
 *
 * @param {string} triageSessionId
 * @param {string} providerNpi
 * @param {'call'|'directions'|'book'} action
 */
export async function logProviderClick(triageSessionId, providerNpi, action) {
  if (!triageSessionId || !providerNpi || !action) return;

  const columnMap = {
    call:       'call_clicked_at',
    directions: 'directions_clicked_at',
    book:       'book_clicked_at',
  };
  const column = columnMap[action];
  if (!column) return;

  try {
    const { error } = await supabase
      .from('route_recommendations')
      .update({ [column]: new Date().toISOString() })
      .eq('triage_session_id', triageSessionId)
      .eq('provider_npi', providerNpi);

    if (error) {
      console.warn('[triageService] logProviderClick error:', error.message);
    }
  } catch (err) {
    console.warn('[triageService] logProviderClick exception:', err.message);
  }
}

/**
 * Record a care outcome from the 48-hour follow-up nudge.
 * Writing this row (yes OR no) acts as the persistent dismissal marker —
 * loadProfile checks for its presence before showing the nudge again.
 *
 * @param {string} triageSessionId
 * @param {string|null} userId
 * @param {boolean} didSeekCare
 */
export async function saveCareOutcome(triageSessionId, userId, didSeekCare) {
  if (!triageSessionId) return;
  try {
    const { error } = await supabase
      .from('care_outcomes')
      .insert({
        triage_session_id: triageSessionId,
        user_id:           userId || null,
        did_seek_care:     didSeekCare,
      });

    if (error) {
      console.warn('[triageService] saveCareOutcome error:', error.message);
    }
  } catch (err) {
    console.warn('[triageService] saveCareOutcome exception:', err.message);
  }
}
